/**
 * Store helpers for consistent state management across Zustand stores
 * Provides unified patterns for loading, error handling, and actions
 */

import { logger } from './logger';

/**
 * Base store state interface
 * All stores should extend this for consistency
 */
export interface BaseStoreState {
  isLoading: boolean;
  error: string | null;
}

/**
 * Wraps async store actions with consistent error handling and loading states
 * Eliminates duplicate try-catch blocks across stores
 * 
 * @example
 * fetchProductos: async (empresaId: string) => {
 *   return handleStoreAction(
 *     async () => {
 *       const { data, error } = await fetchByEmpresaId('productos', empresaId);
 *       if (error) throw error;
 *       set({ productos: data });
 *     },
 *     (error) => set({ error }),
 *     (loading) => set({ isLoading: loading })
 *   );
 * }
 */
export async function handleStoreAction<T = void>(
  action: () => Promise<T>,
  setError: (error: string | null) => void,
  setLoading: (loading: boolean) => void,
  options: {
    successMessage?: string;
    errorPrefix?: string;
  } = {}
): Promise<T | null> {
  setLoading(true);
  setError(null);

  try {
    const result = await action();
    setLoading(false);
    
    if (options.successMessage) {
      logger.debug(options.successMessage);
    }
    
    return result;
    
  } catch (error) {
    const errorMessage = error instanceof Error 
      ? error.message 
      : 'An unexpected error occurred';
    
    const finalMessage = options.errorPrefix 
      ? `${options.errorPrefix}: ${errorMessage}`
      : errorMessage;
    
    setError(finalMessage);
    setLoading(false);
    logger.error('Store action failed:', error);
    
    return null;
  }
}

/**
 * Creates a standardized fetch action for stores
 * Reduces boilerplate for common fetch operations
 * 
 * @example
 * const fetchProductos = createFetchAction<Producto>(
 *   'productos',
 *   (data) => set({ productos: data }),
 *   (loading) => set({ isLoading: loading }),
 *   (error) => set({ error })
 * );
 */
export function createFetchAction<T>(
  resourceName: string,
  setData: (data: T[]) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
) {
  return async (empresaId: string, options?: any): Promise<T[] | null> => {
    return handleStoreAction(
      async () => {
        const { fetchByEmpresaId } = await import('./supabaseHelpers');
        const { data, error } = await fetchByEmpresaId<T>(resourceName, empresaId, options);
        
        if (error) {
          throw new Error(error.message || `Failed to fetch ${resourceName}`);
        }
        
        setData(data || []);
        return data;
      },
      setError,
      setLoading,
      {
        successMessage: `✅ ${resourceName} loaded successfully`,
        errorPrefix: `Error loading ${resourceName}`
      }
    );
  };
}

/**
 * Creates a standardized create action for stores
 * 
 * @example
 * const createProducto = createCreateAction<Producto>(
 *   'productos',
 *   (producto) => set((state) => ({ productos: [...state.productos, producto] })),
 *   (loading) => set({ isLoading: loading }),
 *   (error) => set({ error })
 * );
 */
export function createCreateAction<T>(
  resourceName: string,
  addToState: (item: T) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
) {
  return async (empresaId: string, data: Partial<T>): Promise<T | null> => {
    return handleStoreAction(
      async () => {
        const { createForEmpresa } = await import('./supabaseHelpers');
        const { data: created, error } = await createForEmpresa<T>(resourceName, empresaId, data);
        
        if (error) {
          throw new Error(error.message || `Failed to create ${resourceName}`);
        }
        
        if (created) {
          addToState(created);
        }
        
        return created;
      },
      setError,
      setLoading,
      {
        successMessage: `✅ ${resourceName} created successfully`,
        errorPrefix: `Error creating ${resourceName}`
      }
    );
  };
}

/**
 * Creates a standardized update action for stores
 * 
 * @example
 * const updateProducto = createUpdateAction<Producto>(
 *   'productos',
 *   (updated) => set((state) => ({
 *     productos: state.productos.map(p => p.id === updated.id ? updated : p)
 *   })),
 *   (loading) => set({ isLoading: loading }),
 *   (error) => set({ error })
 * );
 */
export function createUpdateAction<T extends { id: string }>(
  resourceName: string,
  updateInState: (item: T) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
) {
  return async (id: string, updates: Partial<T>): Promise<T | null> => {
    return handleStoreAction(
      async () => {
        const { updateById } = await import('./supabaseHelpers');
        const { data: updated, error } = await updateById<T>(resourceName, id, updates);
        
        if (error) {
          throw new Error(error.message || `Failed to update ${resourceName}`);
        }
        
        if (updated) {
          updateInState(updated);
        }
        
        return updated;
      },
      setError,
      setLoading,
      {
        successMessage: `✅ ${resourceName} updated successfully`,
        errorPrefix: `Error updating ${resourceName}`
      }
    );
  };
}

/**
 * Creates a standardized delete action for stores
 * 
 * @example
 * const deleteProducto = createDeleteAction(
 *   'productos',
 *   (id) => set((state) => ({
 *     productos: state.productos.filter(p => p.id !== id)
 *   })),
 *   (loading) => set({ isLoading: loading }),
 *   (error) => set({ error })
 * );
 */
export function createDeleteAction(
  resourceName: string,
  removeFromState: (id: string) => void,
  setLoading: (loading: boolean) => void,
  setError: (error: string | null) => void
) {
  return async (id: string): Promise<boolean> => {
    const result = await handleStoreAction(
      async () => {
        const { deleteById } = await import('./supabaseHelpers');
        const { error } = await deleteById(resourceName, id);
        
        if (error) {
          throw new Error(error.message || `Failed to delete ${resourceName}`);
        }
        
        removeFromState(id);
        return true;
      },
      setError,
      setLoading,
      {
        successMessage: `✅ ${resourceName} deleted successfully`,
        errorPrefix: `Error deleting ${resourceName}`
      }
    );
    
    return result !== null;
  };
}

/**
 * Validates that an empresaId exists
 * Throws descriptive error if not
 */
export function validateEmpresaId(empresaId: string | undefined | null): string {
  if (!empresaId) {
    throw new Error(
      'No empresa selected. Please select a company from the dropdown menu.'
    );
  }
  return empresaId;
}

/**
 * Type guard for checking if an error is a Supabase error
 */
export function isSupabaseError(error: any): error is { message: string; code: string; details: string } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'message' in error &&
    'code' in error
  );
}

/**
 * Formats Supabase errors into user-friendly messages
 */
export function formatSupabaseError(error: any): string {
  if (isSupabaseError(error)) {
    // Common Supabase error codes
    switch (error.code) {
      case '23505': // Unique violation
        return 'This record already exists. Please use a different identifier.';
      case '23503': // Foreign key violation
        return 'Cannot complete action - this item is referenced by other records.';
      case '42P01': // Table doesn't exist
        return 'Database configuration error. Please contact support.';
      case 'PGRST116': // No rows found
        return 'Record not found.';
      default:
        return error.message || 'An database error occurred';
    }
  }
  
  if (error instanceof Error) {
    return error.message;
  }
  
  return 'An unexpected error occurred';
}



